import React, { useEffect, useRef, useState } from 'react';
import { themeVariables } from '@noths/polaris-design-system-abstract-theme';
import dynamic from 'next/dynamic';

import { VideoPlayIndicator } from 'src/components/atoms/VideoPlayIndicator/VideoPlayIndicator';
import type { MediaLocation } from 'src/components/organisms/ProductMediaCarousel/types';
import {
  trackProductVideoComplete,
  trackProductVideoExpand,
  trackProductVideoImpression,
  trackProductVideoPause,
  trackProductVideoPlay,
} from 'src/tracking/trackProductVideoEvents';
import { FullscreenIcon, PauseIcon, PlayIcon } from './icons';
import * as styles from './ProductVideoSlide.styles';

const MuxPlayer = dynamic(() => import('@mux/mux-player-react'), { ssr: false });

export interface ProductVideoSlideProps {
  /** 1-based carousel position of the video; sent with every analytics event. */
  contentPosition: number;
  /**
   * True when this is the carousel's current slide. The player is mounted only
   * while the slide is active; leaving and returning remounts it, so playback
   * always restarts from the beginning. While inactive, only the poster shows —
   * which keeps the player (and its video bytes) off the initial page load.
   */
  isActive: boolean;
  /**
   * Carousel context for analytics: `'Main media'` for the main carousel,
   * `'Expanded media'` for the full-screen modal. Tags every event, and gates
   * the impression so it fires only in the main carousel (never full-screen).
   */
  location: MediaLocation;
  /** When provided, an expand control opens the full-screen view. Omitted in the modal. */
  onExpand?: () => void;
  playbackId: string;
  posterUrl: string;
  title: string;
  /** Total media count in the carousel; sent with the impression event. */
  totalOptions?: number;
}

// The brand purple used across the app (theme `content.brand`).
const PLAYER_ACCENT_COLOR = `var(${themeVariables.colors.content.brand})`;

// How long the pause button stays over a playing video before it fades out of the way.
const CONTROLS_HIDE_DELAY_MS = 3000;

export const ProductVideoSlide = ({
  contentPosition,
  isActive,
  location,
  onExpand,
  playbackId,
  posterUrl,
  title,
  totalOptions,
}: ProductVideoSlideProps) => {
  const [paused, setPaused] = useState(false);
  const [ready, setReady] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  // Guards the once-per-impression complete event; reset when the slide reactivates.
  const hasCompletedRef = useRef(false);

  // Reset playback state whenever the slide (de)activates: the player remounts,
  // so it should start unpaused and fade in fresh once it begins playing. Fire
  // the impression when the video becomes the active slide — but only in the main
  // carousel, so a single product view isn't counted again from the full-screen modal.
  useEffect(() => {
    setPaused(false);
    setReady(false);
    setControlsVisible(true);
    hasCompletedRef.current = false;

    if (isActive && location === 'Main media') {
      trackProductVideoImpression({
        contentPosition,
        location,
        playbackId,
        totalOptions: totalOptions ?? 0,
      });
    }
  }, [contentPosition, isActive, location, playbackId, totalOptions]);

  const isPlaying = ready && !paused;

  // The pause button gets out of the way only while the video is actually playing, so a
  // paused video, or one whose autoplay the browser refused, always shows its control.
  useEffect(() => {
    if (!isActive || !isPlaying || !controlsVisible) {
      return undefined;
    }

    const timeout = setTimeout(() => setControlsVisible(false), CONTROLS_HIDE_DELAY_MS);

    return () => clearTimeout(timeout);
  }, [controlsVisible, isActive, isPlaying]);

  // Manual toggle only — autoplay is represented by the impression, never a play event.
  const togglePlay = () => {
    const willPause = !paused;
    setPaused(willPause);
    setControlsVisible(true);

    if (willPause) {
      trackProductVideoPause({ contentPosition, location, playbackId });
    } else {
      trackProductVideoPlay({ contentPosition, location, playbackId });
    }
  };

  // Looping is done by hand (rather than the native `loop` attribute) so the
  // `ended` event fires: on the first end we record the completion, then restart
  // playback to keep the silent preview looping.
  // The browser pauses by itself when it blocks autoplay, so follow the player rather than
  // assume it is playing. The pause it fires at the end of each loop is skipped, since
  // handleVideoEnded restarts playback straight away.
  const handlePlayerPause = (event: Event) => {
    if (!(event.target as HTMLMediaElement | null)?.ended) {
      setPaused(true);
    }
  };

  const handleVideoEnded = (event: Event) => {
    if (!hasCompletedRef.current) {
      hasCompletedRef.current = true;
      trackProductVideoComplete({ contentPosition, location, playbackId });
    }

    const player = event.target as HTMLMediaElement | null;

    if (player) {
      player.currentTime = 0;
      void player.play();
    }
  };

  return (
    // Tapping the video only brings the controls back into view. It is pointer-only on
    // purpose: the pause button is always in the tab order, so keyboard users never need it.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div css={styles.videoSlide} onClick={isActive ? () => setControlsVisible(true) : undefined}>
      {/* Poster is the base layer; the player fades in over it once it plays. */}
      <img alt={title} css={styles.poster} src={posterUrl} />
      {/* While the poster is all there is, it is indistinguishable from a photo, so say it is
          a video. Once the player takes over, the pause button takes the badge's place. */}
      {!isActive && <VideoPlayIndicator size="lg" />}
      {isActive && (
        <>
          <MuxPlayer
            accentColor={PLAYER_ACCENT_COLOR}
            autoPlay
            css={styles.player}
            metadata={{ player_name: 'browse-productdetails', video_title: title }}
            muted
            nohotkeys
            onEnded={handleVideoEnded}
            onPause={handlePlayerPause}
            onPlay={() => setPaused(false)}
            onPlaying={() => setReady(true)}
            paused={paused}
            playbackId={playbackId}
            playsInline
            streamType="on-demand"
            style={{ opacity: ready ? 1 : 0 }}
            // Its own controls are hidden, so a focus stop here would announce nothing. Lowercase
            // on purpose: the wrapper turns camelCase props into kebab-case attributes, so
            // tabIndex would become a meaningless tab-index.
            tabindex={-1}
            thumbnailTime={0}
          />
          <button
            aria-label={paused ? 'Play video' : 'Pause video'}
            css={styles.playPauseButton}
            data-visible={controlsVisible || !isPlaying}
            onClick={(event) => {
              event.stopPropagation();
              togglePlay();
            }}
            type="button"
          >
            {paused ? <PlayIcon /> : <PauseIcon />}
          </button>
          {onExpand && (
            <button
              aria-label="View video full screen"
              css={styles.expandButton}
              onClick={(event) => {
                event.stopPropagation();
                trackProductVideoExpand({ contentPosition, location, playbackId });
                onExpand();
              }}
              type="button"
            >
              <FullscreenIcon />
            </button>
          )}
        </>
      )}
    </div>
  );
};
